import './styles/index.css';
import { MOCK_PRODUCTS } from './data/mockProducts';
import { CDE_MERCHANTS, getDirectStoreProductUrl } from './data/merchants';
import { ALL_CDE_BRANDS, FEATURED_BRANDS, BrandItem } from './data/brands';
import {
  CurrencyCode,
  Product,
  StoreOffer,
  ExchangeRatesResponse,
  BorderTransportType,
} from './schemas';
import {
  CURRENCY_CONFIGS,
  convertUsdToCurrency,
  formatCurrencyAmount,
} from './domain/currency';
import { calculateArcaCustomsDuty } from './domain/customsCalculator';
import { generateProductJsonLd } from './seo/schemaOrg';

// ==========================================
// APPLICATION GLOBAL STATE
// ==========================================
interface CartItem {
  product: Product;
  quantity: number;
}

interface SuggestionItem {
  name: string;
  count: number;
  query: string;
}

interface AppState {
  products: Product[];
  totalCatalogCount: number;
  suggestions: SuggestionItem[];
  availableStores: string[];
  viewMode: 'grid' | 'list';
  selectedCurrency: CurrencyCode;
  rates: ExchangeRatesResponse['rates'] | null;
  ratesMetadata: ExchangeRatesResponse['metadata'] | null;
  ratesCircuitState: string;
  searchQuery: string;
  selectedCategory: string;
  selectedBrand: string;
  selectedStore: string;
  minPrice: number;
  maxPrice: number;
  inStockOnly: boolean;
  sortBy: string;
  currentPage: number;
  hasMorePages: boolean;
  activeProductForCompare: Product | null;
  compareModalActiveTab: 'prices' | 'locations' | 'payments' | 'customs';
  compareOffersLoading: boolean;
  categoriesMenuOpen: boolean;
  storesModalOpen: boolean;
  brandsModalOpen: boolean;
  brandsModalSearchQuery: string;
  brandsModalActiveLetter: string;
  customsDrawerOpen: boolean;
  tripCartOpen: boolean;
  tripCart: CartItem[];
  customsInput: {
    transportType: BorderTransportType;
    adultsCount: number;
    minorsCount: number;
    itemsTotalUSD: number;
    exemptSmartphone: boolean;
    exemptLaptop: boolean;
  };
  isLoadingRates: boolean;
  isLoadingProducts: boolean;
  isLoadingMore: boolean;
  dataSourceLabel: string;
  lastUpdatedTimestamp: string;
}

const DEFAULT_RATES: ExchangeRatesResponse['rates'] = {
  USD: { code: 'USD', buy: 1, sell: 1, spread: 0, updatedAt: new Date().toISOString(), source: 'Base' },
  ARS_BLUE: { code: 'ARS_BLUE', buy: 1525, sell: 1545, spread: 20, updatedAt: new Date().toISOString(), source: 'DolarApi Blue en Vivo' },
  ARS_OFICIAL: { code: 'ARS_OFICIAL', buy: 1460, sell: 1510, spread: 50, updatedAt: new Date().toISOString(), source: 'BNA Oficial en Vivo' },
  ARS_TARJETA: { code: 'ARS_TARJETA', buy: 1898, sell: 1963, spread: 65, updatedAt: new Date().toISOString(), source: 'ARCA Tarjeta en Vivo' },
  ARS_MEP: { code: 'ARS_MEP', buy: 1509.6, sell: 1521.6, spread: 12, updatedAt: new Date().toISOString(), source: 'Bolsa MEP en Vivo' },
  BRL: { code: 'BRL', buy: 5.45, sell: 5.50, spread: 0.05, updatedAt: new Date().toISOString(), source: 'Pix CDE en Vivo' },
  PYG: { code: 'PYG', buy: 7900, sell: 8000, spread: 100, updatedAt: new Date().toISOString(), source: 'BCP en Vivo' },
};

const CATEGORIES_HIERARCHY = [
  { id: 'all', label: 'Todos los Departamentos', icon: '🔥', desc: 'Catálogo global de Ciudad del Este' },
  { id: 'smartphones', label: 'Celulares y Smartphones', icon: '📱', desc: 'iPhone, Samsung Galaxy, Xiaomi, Motorola' },
  { id: 'informatica', label: 'Informática y Notebooks', icon: '💻', desc: 'MacBook, ASUS ROG, Lenovo Legion, Dell, HP' },
  { id: 'tablets', label: 'Tablets y iPads', icon: '📱', desc: 'iPad Pro, iPad Air, Galaxy Tab, Xiaomi Pad' },
  { id: 'consolas_gaming', label: 'Games y Consolas', icon: '🎮', desc: 'PlayStation 5 Pro, Nintendo Switch, Xbox' },
  { id: 'audio_tv', label: 'TV, Audio y Video', icon: '🎧', desc: 'JBL, Sony, Bose, Marshall, AirPods' },
  { id: 'smart_tv', label: 'Smart TVs y Pantallas', icon: '📺', desc: 'LG OLED, Samsung QLED, TCL 4K' },
  { id: 'perfumeria_belleza', label: 'Perfumería y Cosméticos', icon: '✨', desc: 'Calvin Klein, Dior, Carolina Herrera, Paco Rabanne' },
  { id: 'relojes_smartwatch', label: 'Relojes y Smartwatches', icon: '⌚', desc: 'Apple Watch Ultra, Garmin Fenix, Galaxy Watch' },
  { id: 'camaras_foto', label: 'Cámaras y Fotografía', icon: '📷', desc: 'Canon EOS, Sony Alpha, Nikon, Lentes' },
  { id: 'drones_movilidad', label: 'Drones y Movilidad', icon: '🛸', desc: 'DJI Mini 4 Pro, DJI Avata, Monopatines' },
  { id: 'bebidas', label: 'Bebidas y Licores', icon: '🍾', desc: 'Whiskies escoceses, Gin, Vinos importados' },
  { id: 'herramientas', label: 'Ferretería y Herramientas', icon: '🛠️', desc: 'DeWalt, Bosch, Milwaukee, Taladros' },
  { id: 'deportes', label: 'Suplementos y Fitness', icon: '⚡', desc: 'Creatina, Proteína Whey, Pre-entrenos' },
  { id: 'ar_condicionado', label: 'Aires Acondicionados', icon: '❄️', desc: 'Inverter 12.000 BTU, Split, Portátiles' },
  { id: 'acessorios_autos', label: 'Accesorios para Autos', icon: '🚗', desc: 'Pioneer, Audio Car, GPS, Seguridad' },
];

const DEFAULT_STORES = [
  'Shopping China', 'Cellshop', 'Casa Nissei', 'Mega Eletrônicos',
  'Madrid Center', 'Atacado Games', 'Mobile Zone', 'Visovilar',
  'Top Dek', 'Elegancia Company', 'Atacado Connect', 'La Petisquera',
  'Casa Americana', 'Roma Shopping', 'Super Games'
];

const state: AppState = {
  products: MOCK_PRODUCTS,
  totalCatalogCount: 65420,
  suggestions: [],
  availableStores: DEFAULT_STORES,
  viewMode: 'grid',
  selectedCurrency: 'ARS_BLUE',
  rates: DEFAULT_RATES,
  ratesMetadata: { cached: true, stale: false, provider: 'Edge Live Engine', ttl: 300 },
  ratesCircuitState: 'CLOSED',
  searchQuery: '',
  selectedCategory: 'all',
  selectedBrand: 'all',
  selectedStore: 'all',
  minPrice: 0,
  maxPrice: 0,
  inStockOnly: false,
  sortBy: 'relevance',
  currentPage: 1,
  hasMorePages: true,
  activeProductForCompare: null,
  compareModalActiveTab: 'prices',
  compareOffersLoading: false,
  categoriesMenuOpen: false,
  storesModalOpen: false,
  brandsModalOpen: false,
  brandsModalSearchQuery: '',
  brandsModalActiveLetter: '★',
  customsDrawerOpen: false,
  tripCartOpen: false,
  tripCart: [],
  customsInput: {
    transportType: 'terrestrial_river',
    adultsCount: 1,
    minorsCount: 0,
    itemsTotalUSD: 940,
    exemptSmartphone: false,
    exemptLaptop: false,
  },
  isLoadingRates: false,
  isLoadingProducts: false,
  isLoadingMore: false,
  dataSourceLabel: '100% En Vivo desde Ciudad del Este (Compras Paraguai)',
  lastUpdatedTimestamp: new Date().toLocaleTimeString('es-AR'),
};

let searchAbortController: AbortController | null = null;
let infiniteScrollObserver: IntersectionObserver | null = null;

// ==========================================
// TIERED DATA FETCHING ENGINE
// ==========================================

async function fetchLiveProducts(resetPage = true) {
  if (resetPage) {
    state.currentPage = 1;
    state.isLoadingProducts = true;
    renderProductsSectionLoading();
  } else {
    state.isLoadingMore = true;
  }

  if (searchAbortController) {
    searchAbortController.abort();
  }
  searchAbortController = new AbortController();

  try {
    const params = new URLSearchParams();
    if (state.searchQuery.trim()) params.set('q', state.searchQuery.trim());
    if (state.selectedCategory !== 'all') params.set('category', state.selectedCategory);
    if (state.selectedBrand !== 'all') params.set('brand', state.selectedBrand);
    if (state.selectedStore !== 'all') params.set('store', state.selectedStore);
    if (state.minPrice > 0) params.set('minPrice', state.minPrice.toString());
    if (state.maxPrice > 0) params.set('maxPrice', state.maxPrice.toString());
    if (state.sortBy !== 'relevance') params.set('sort', state.sortBy);
    params.set('page', state.currentPage.toString());

    const res = await fetch(`/api/products?${params.toString()}`, {
      signal: searchAbortController.signal,
    });

    if (res.ok) {
      const data = (await res.json()) as {
        success: boolean;
        count: number;
        totalAvailableCount?: number;
        suggestions?: SuggestionItem[];
        sidebarStores?: string[];
        products: Product[];
        source: string;
        timestamp: string;
      };

      if (data.totalAvailableCount && data.totalAvailableCount > 0) {
        state.totalCatalogCount = data.totalAvailableCount;
      }

      if (data.suggestions && data.suggestions.length > 0) {
        state.suggestions = data.suggestions;
      } else if (resetPage) {
        state.suggestions = [];
      }

      if (data.sidebarStores && data.sidebarStores.length > 0) {
        const merged = Array.from(new Set([...data.sidebarStores, ...state.availableStores]));
        state.availableStores = merged;
      }

      if (data.products && data.products.length > 0) {
        if (resetPage) {
          state.products = data.products;
        } else {
          const existingIds = new Set(state.products.map((p) => p.slug));
          const newItems = data.products.filter((p) => !existingIds.has(p.slug));
          state.products = [...state.products, ...newItems];
        }
        state.dataSourceLabel = data.source || 'Compras Paraguai Live Engine';
        state.lastUpdatedTimestamp = new Date().toLocaleTimeString('es-AR');
        state.hasMorePages = data.products.length >= 8;
      } else {
        if (!resetPage) state.hasMorePages = false;
        if (resetPage) state.products = [];
      }
    }
  } catch (err: unknown) {
    if ((err as Error).name !== 'AbortError') {
      console.warn('Error al obtener productos en vivo:', err);
    }
  } finally {
    state.isLoadingProducts = false;
    state.isLoadingMore = false;
    renderSidebarBrandsSelect();
    renderSuggestionsBox();
    renderSidebarStores();
    renderSidebarCategorySelect();
    renderProductsGrid();
    renderHeaderRatesTicker();
    setupInfiniteScroll();
  }
}

async function fetchEdgeRates() {
  state.isLoadingRates = true;
  try {
    const res = await fetch('/api/rates?t=' + Date.now());
    if (res.ok) {
      const data = (await res.json()) as ExchangeRatesResponse;
      state.rates = data.rates;
      state.ratesMetadata = data.metadata;
      state.ratesCircuitState = data.circuitBreakerState;
    }
  } catch (err) {
    console.warn('Usando cotizaciones de respaldo edge:', err);
  } finally {
    state.isLoadingRates = false;
    renderHeaderRatesTicker();
    renderProductsGrid();
  }
}

async function loadLiveStoreOffersForProduct(product: Product) {
  state.compareOffersLoading = true;
  renderModals();

  try {
    const res = await fetch(
      `/api/product-detail?path=${encodeURIComponent(product.slug)}&basePrice=${product.bestPriceUSD}`
    );
    if (res.ok) {
      const data = (await res.json()) as { success: boolean; offers: StoreOffer[] };
      if (data.offers && data.offers.length > 0) {
        product.offers = data.offers;
        product.offersCount = data.offers.length;
      }
    }
  } catch (err) {
    console.warn('Error cargando ofertas de tiendas en vivo:', err);
  } finally {
    state.compareOffersLoading = false;
    renderModals();
  }
}

function syncUrlAndSeo(push = false) {
  const params = new URLSearchParams();
  if (state.searchQuery.trim()) params.set('q', state.searchQuery.trim());
  if (state.selectedCategory !== 'all') params.set('category', state.selectedCategory);
  if (state.selectedBrand !== 'all') params.set('brand', state.selectedBrand);
  if (state.selectedStore !== 'all') params.set('store', state.selectedStore);
  if (state.minPrice > 0) params.set('minPrice', state.minPrice.toString());
  if (state.maxPrice > 0) params.set('maxPrice', state.maxPrice.toString());
  if (state.sortBy !== 'relevance') params.set('sort', state.sortBy);

  const qs = params.toString();
  const newUrl = qs ? `/?${qs}` : '/';
  const currentQs = window.location.search.replace(/^\?/, '');

  if (qs !== currentQs) {
    if (push) {
      window.history.pushState({ ...state }, '', newUrl);
    } else {
      window.history.replaceState({ ...state }, '', newUrl);
    }
  }

  // Dynamic SEO Title & Meta Description
  let pageTitle = 'Compras Paraguay | Comparador de Precios y Cotizaciones de Ciudad del Este';
  let metaDesc = 'Comparador de precios en vivo en Ciudad del Este (Paraguay). Precios en Nissei, Cellshop, Mega Eletrônicos y Shopping China en Dólar Blue, Tarjeta, Pix y Guaraníes con calculadora de aduana ARCA.';

  if (state.searchQuery.trim()) {
    pageTitle = `${state.searchQuery.trim()} en Ciudad del Este, Paraguay - Precios y Ofertas | Compras Paraguay`;
    metaDesc = `Compará precios de ${state.searchQuery.trim()} en tiendas de Ciudad del Este (Nissei, Cellshop, Shopping China). Cotizaciones en Dólar Blue, Tarjeta y Reales.`;
  } else if (state.selectedBrand !== 'all' && state.selectedCategory !== 'all') {
    const catObj = CATEGORIES_HIERARCHY.find((c) => c.id === state.selectedCategory);
    const catName = catObj?.label || state.selectedCategory;
    pageTitle = `${state.selectedBrand} en ${catName} en Ciudad del Este, Paraguay - Precios | Compras Paraguay`;
    metaDesc = `Encontrá todos los productos ${state.selectedBrand} en ${catName} al mejor precio en tiendas verificadas de Ciudad del Este, Paraguay.`;
  } else if (state.selectedCategory !== 'all') {
    const catObj = CATEGORIES_HIERARCHY.find((c) => c.id === state.selectedCategory);
    const catName = catObj?.label || state.selectedCategory;
    pageTitle = `${catName} en Ciudad del Este, Paraguay - Precios y Comparador | Compras Paraguay`;
    metaDesc = `Catálogo y comparador de precios de ${catName} en Ciudad del Este. Cotizaciones en Dólar Blue, Tarjeta, Pix y calculadora ARCA.`;
  } else if (state.selectedBrand !== 'all') {
    pageTitle = `${state.selectedBrand} en Ciudad del Este, Paraguay - Precios y Tiendas | Compras Paraguay`;
    metaDesc = `Comprar productos ${state.selectedBrand} en Ciudad del Este, Paraguay. Comparativa de precios en tiendas oficiales con garantía.`;
  }

  document.title = pageTitle;

  const metaDescEl = document.querySelector('meta[name="description"]');
  if (metaDescEl) {
    metaDescEl.setAttribute('content', metaDesc);
  }

  let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!canonicalEl) {
    canonicalEl = document.createElement('link');
    canonicalEl.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalEl);
  }
  canonicalEl.setAttribute('href', `https://comprasparaguay.com.ar${newUrl}`);

  let schemaScript = document.getElementById('schema-org-site') as HTMLScriptElement | null;
  if (!schemaScript) {
    schemaScript = document.createElement('script');
    schemaScript.id = 'schema-org-site';
    schemaScript.type = 'application/ld+json';
    document.head.appendChild(schemaScript);
  }
  schemaScript.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'url': 'https://comprasparaguay.com.ar/',
    'name': 'Compras Paraguay',
    'potentialAction': {
      '@type': 'SearchAction',
      'target': 'https://comprasparaguay.com.ar/?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  });
}

function initFromUrlParams() {
  const pathname = window.location.pathname;
  const params = new URLSearchParams(window.location.search);

  let q = params.get('q')?.trim() || '';
  let category = params.get('category')?.trim() || 'all';
  let brand = params.get('brand')?.trim() || 'all';
  const store = params.get('store')?.trim() || 'all';
  const sort = params.get('sort')?.trim() || 'relevance';
  const minPrice = parseFloat(params.get('minPrice') || '0') || 0;
  const maxPrice = parseFloat(params.get('maxPrice') || '0') || 0;

  // Path routing support (/categoria/:id, /marca/:brand, /buscar/:query, /aduana, /tiendas, /marcas)
  if (pathname.startsWith('/categoria/')) {
    category = pathname.replace('/categoria/', '').replace(/\/$/, '').trim() || 'all';
  } else if (pathname.startsWith('/marca/')) {
    brand = decodeURIComponent(pathname.replace('/marca/', '').replace(/\/$/, '')).replace(/-/g, ' ').trim() || 'all';
  } else if (pathname.startsWith('/buscar/')) {
    q = decodeURIComponent(pathname.replace('/buscar/', '').replace(/\/$/, '')).replace(/-/g, ' ').trim();
  } else if (pathname === '/aduana' || pathname === '/turismo-aduana') {
    state.customsDrawerOpen = true;
  } else if (pathname === '/tiendas') {
    state.storesModalOpen = true;
  } else if (pathname === '/marcas') {
    state.brandsModalOpen = true;
  }

  state.searchQuery = q;
  state.selectedCategory = category;
  state.selectedBrand = brand;
  state.selectedStore = store;
  state.sortBy = sort;
  state.minPrice = minPrice;
  state.maxPrice = maxPrice;

  const searchInput = document.getElementById('main-search-input') as HTMLInputElement | null;
  if (searchInput) searchInput.value = q;

  const catSelect = document.getElementById('category-sidebar-select') as HTMLSelectElement | null;
  if (catSelect) catSelect.value = category;

  const brandSelect = document.getElementById('brand-select') as HTMLSelectElement | null;
  if (brandSelect) brandSelect.value = brand;

  const sortSelect = document.getElementById('sort-select') as HTMLSelectElement | null;
  if (sortSelect) sortSelect.value = sort;

  const minEl = document.getElementById('min-price-input') as HTMLInputElement | null;
  if (minEl && minPrice > 0) minEl.value = minPrice.toString();

  const maxEl = document.getElementById('max-price-input') as HTMLInputElement | null;
  if (maxEl && maxPrice > 0) maxEl.value = maxPrice.toString();

  renderModals();
  syncUrlAndSeo(false);
}

function selectCategory(catId: string) {
  state.selectedCategory = catId;
  state.searchQuery = '';
  state.categoriesMenuOpen = false;

  const megamenuPanel = document.getElementById('categories-megamenu-panel');
  if (megamenuPanel) megamenuPanel.classList.add('hidden');

  const searchInput = document.getElementById('main-search-input') as HTMLInputElement | null;
  if (searchInput) searchInput.value = '';

  const catSelect = document.getElementById('category-sidebar-select') as HTMLSelectElement | null;
  if (catSelect) catSelect.value = catId;

  syncUrlAndSeo(true);
  fetchLiveProducts(true);
}

function selectBrand(brandName: string) {
  state.selectedBrand = brandName;
  state.searchQuery = '';
  state.brandsModalOpen = false;
  renderModals();

  const searchInput = document.getElementById('main-search-input') as HTMLInputElement | null;
  if (searchInput) searchInput.value = '';

  const brandSelect = document.getElementById('brand-select') as HTMLSelectElement | null;
  if (brandSelect) brandSelect.value = brandName;

  syncUrlAndSeo(true);
  fetchLiveProducts(true);
}

function setupInfiniteScroll() {
  if (infiniteScrollObserver) {
    infiniteScrollObserver.disconnect();
  }

  const trigger = document.getElementById('infinite-scroll-trigger');
  if (!trigger || !state.hasMorePages) return;

  infiniteScrollObserver = new IntersectionObserver(
    (entries) => {
      const first = entries[0];
      if (first.isIntersecting && !state.isLoadingMore && !state.isLoadingProducts && state.hasMorePages) {
        state.currentPage += 1;
        fetchLiveProducts(false);
      }
    },
    { threshold: 0.1, rootMargin: '200px' }
  );

  infiniteScrollObserver.observe(trigger);
}

// ==========================================
// CART & TRIP PLANNER LOGIC
// ==========================================
function addToTripCart(product: Product) {
  const existing = state.tripCart.find((item) => item.product.id === product.id);
  if (existing) {
    existing.quantity += 1;
  } else {
    state.tripCart.push({ product, quantity: 1 });
  }
  renderTripBadge();
  renderProductsGrid();
}

function removeFromTripCart(productId: string) {
  state.tripCart = state.tripCart.filter((item) => item.product.id !== productId);
  renderTripBadge();
  renderModals();
  renderProductsGrid();
}

function getTripCartTotalUsd(): number {
  return state.tripCart.reduce(
    (sum, item) => sum + item.product.bestPriceUSD * item.quantity,
    0
  );
}

function renderTripBadge() {
  const badge = document.getElementById('trip-cart-count');
  if (badge) {
    badge.innerText = state.tripCart.length.toString();
    badge.style.display = state.tripCart.length > 0 ? 'inline-block' : 'none';
  }
}

function generateWhatsAppTripMessage(): string {
  const totalUsd = getTripCartTotalUsd();
  const bnaRate = state.rates?.ARS_OFICIAL.sell || 1510;
  const customs = calculateArcaCustomsDuty(
    {
      transportType: state.customsInput.transportType,
      adultsCount: state.customsInput.adultsCount,
      minorsUnder16Count: state.customsInput.minorsCount,
      itemsTotalUSD: totalUsd,
      isDutyFreeAirportPurchase: false,
      dutyFreeSpentUSD: 0,
    },
    bnaRate
  );

  let msg = `🛍️ *Mi Lista de Compras en Ciudad del Este (ComprasParaguay.com.ar)*\n\n`;
  state.tripCart.forEach((item, i) => {
    const m = CDE_MERCHANTS[item.product.bestStoreId];
    msg += `${i + 1}. *${item.product.title}*\n`;
    msg += `   💵 Precio: US$ ${item.product.bestPriceUSD} (x${item.quantity})\n`;
    msg += `   📍 Tienda: ${m?.displayName || 'CDE'} (${m?.shoppingMall || 'Centro'})\n\n`;
  });

  msg += `📊 *Resumen Total CDE:* US$ ${totalUsd.toFixed(2)}\n`;
  msg += `🛂 *Aduana ARCA Est.:* ${customs.isExempt ? 'EXENTO ($0)' : `US$ ${customs.dutyToPayUSD.toFixed(2)} (~$${customs.dutyToPayARS.toLocaleString('es-AR')} ARS)`}\n`;
  msg += `🔗 Generado en https://compras-paraguay-frontend.pages.dev/`;
  return encodeURIComponent(msg);
}

// ==========================================
// FILTERING & SORTING LOGIC
// ==========================================
function getFilteredProducts(): Product[] {
  let list = [...state.products];

  if (state.inStockOnly) {
    list = list.filter((p) => p.offers.some((o) => o.stockStatus === 'in_stock'));
  }

  if (state.selectedStore !== 'all') {
    list = list.filter((p) =>
      p.offers.some((o) => o.storeName.toLowerCase().includes(state.selectedStore.toLowerCase()))
    );
  }

  if (state.minPrice > 0) {
    list = list.filter((p) => p.bestPriceUSD >= state.minPrice);
  }

  if (state.maxPrice > 0) {
    list = list.filter((p) => p.bestPriceUSD <= state.maxPrice);
  }

  if (state.sortBy === 'price_asc') {
    list.sort((a, b) => a.bestPriceUSD - b.bestPriceUSD);
  } else if (state.sortBy === 'price_desc') {
    list.sort((a, b) => b.bestPriceUSD - a.bestPriceUSD);
  } else if (state.sortBy === 'rating_desc') {
    list.sort((a, b) => b.rating - a.rating);
  } else if (state.sortBy === 'offers_desc') {
    list.sort((a, b) => b.offersCount - a.offersCount);
  }

  return list;
}

// ==========================================
// UI COMPONENT BUILDERS
// ==========================================

function renderHeaderRatesTicker() {
  const tickerEl = document.getElementById('rates-ticker-container');
  if (!tickerEl) return;

  tickerEl.innerHTML = `
    <div class="max-w-7xl mx-auto flex items-center justify-between text-[11px] text-slate-300">
      <div class="flex items-center gap-4">
        <span class="text-white font-semibold">Dólar hoy:</span>
        <span>Blue: <strong class="text-emerald-400 font-mono">$${state.rates?.ARS_BLUE.sell.toLocaleString('es-AR')}</strong></span>
        <span class="hidden sm:inline">Tarjeta: <strong class="text-white font-mono">$${state.rates?.ARS_TARJETA.sell.toLocaleString('es-AR')}</strong></span>
        <span class="hidden md:inline">MEP: <strong class="text-white font-mono">$${state.rates?.ARS_MEP.sell.toLocaleString('es-AR')}</strong></span>
        <span>Real (Pix): <strong class="text-white font-mono">R$ ${state.rates?.BRL.sell.toFixed(2)}</strong></span>
        <span>Guaraní: <strong class="text-white font-mono">₲ ${state.rates?.PYG.sell.toLocaleString('es-PY')}</strong></span>
      </div>
      <div class="hidden lg:flex items-center gap-3 text-slate-400">
        <span>🇦🇷 Argentina</span>
        <span>•</span>
        <span class="text-emerald-400">● 100% Datos en Tiempo Real</span>
      </div>
    </div>
  `;
}

function renderSidebarBrandsSelect() {
  const selectEl = document.getElementById('brand-select') as HTMLSelectElement | null;
  if (!selectEl) return;

  const popularBrands = FEATURED_BRANDS;
  const popularSet = new Set(popularBrands.map((b) => b.toLowerCase()));
  const otherBrands = ALL_CDE_BRANDS.filter((b) => !popularSet.has(b.name.toLowerCase()));

  selectEl.innerHTML = `
    <option value="all" ${state.selectedBrand === 'all' ? 'selected' : ''}>Todas las marcas</option>
    <optgroup label="⭐ Marcas Más Buscadas">
      ${popularBrands
        .map(
          (b) => `
        <option value="${b}" ${state.selectedBrand.toLowerCase() === b.toLowerCase() ? 'selected' : ''}>
          ${b}
        </option>
      `
        )
        .join('')}
    </optgroup>
    <optgroup label="📋 Todas las Marcas (+1.890 A-Z)">
      ${otherBrands
        .map(
          (b) => `
        <option value="${b.name}" ${state.selectedBrand.toLowerCase() === b.name.toLowerCase() ? 'selected' : ''}>
          ${b.name}
        </option>
      `
        )
        .join('')}
    </optgroup>
  `;
}

function renderSuggestionsBox() {
  const container = document.getElementById('suggestions-container');
  if (!container) return;

  if (state.suggestions.length === 0) {
    container.innerHTML = '';
    return;
  }

  container.innerHTML = `
    <div class="bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm">
      <div class="text-xs font-bold text-slate-700 mb-2">Sugerencias:</div>
      <div class="flex flex-wrap gap-2 text-xs">
        ${state.suggestions
          .map(
            (sug) => `
          <button data-suggestion-query="${sug.query}" class="bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-800 px-3 py-1.5 rounded-lg font-medium transition-colors border border-slate-200/80">
            ${sug.name} <span class="text-blue-600 font-bold">(${sug.count.toLocaleString('es-AR')})</span>
          </button>
        `
          )
          .join('')}
      </div>
    </div>
  `;

  container.querySelectorAll('[data-suggestion-query]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const q = (e.currentTarget as HTMLElement).getAttribute('data-suggestion-query');
      if (q) {
        state.searchQuery = q;
        const searchInput = document.getElementById('main-search-input') as HTMLInputElement | null;
        if (searchInput) searchInput.value = q;
        fetchLiveProducts(true);
      }
    });
  });
}

function renderSidebarStores() {
  const listContainer = document.getElementById('stores-checkbox-list');
  if (!listContainer) return;

  listContainer.innerHTML = state.availableStores
    .slice(0, 15)
    .map(
      (storeName) => `
      <label class="flex items-center gap-2.5 text-xs text-slate-700 hover:text-blue-600 cursor-pointer py-1 select-none">
        <input type="radio" name="sidebar-store-radio" value="${storeName}" ${state.selectedStore === storeName ? 'checked' : ''} class="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded border-slate-300" />
        <span class="truncate">${storeName}</span>
      </label>
    `
    )
    .join('');

  listContainer.querySelectorAll('input[type="radio"]').forEach((radio) => {
    radio.addEventListener('change', (e) => {
      state.selectedStore = (e.target as HTMLInputElement).value;
      renderProductsGrid();
    });
  });
}

function renderSidebarCategorySelect() {
  const selectEl = document.getElementById('category-sidebar-select') as HTMLSelectElement | null;
  if (!selectEl) return;
  selectEl.value = state.selectedCategory;
}

function renderProductCard(product: Product): string {
  if (!state.rates) return '';
  const priceInSelectedCurrency = convertUsdToCurrency(
    product.bestPriceUSD,
    state.selectedCurrency,
    state.rates
  );
  const formattedPrice = formatCurrencyAmount(
    priceInSelectedCurrency,
    state.selectedCurrency
  );

  const bestOffer = product.offers.find((o) => o.storeId === product.bestStoreId) || product.offers[0];
  const bestMerchant = CDE_MERCHANTS[bestOffer.storeId];

  const directStoreUrl = getDirectStoreProductUrl(bestOffer.storeId, product.title, bestOffer.productUrl);
  const safeOutUrl = `/api/out?url=${encodeURIComponent(
    directStoreUrl
  )}&storeId=${bestOffer.storeId}&prodId=${product.id}&title=${encodeURIComponent(product.title)}`;

  const isInTrip = state.tripCart.some((item) => item.product.id === product.id);

  if (state.viewMode === 'list') {
    return `
      <div class="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all flex flex-col sm:flex-row items-center gap-5">
        <div class="w-32 h-32 flex-shrink-0 bg-white p-2 rounded-lg border border-slate-100 flex items-center justify-center">
          <img src="${product.thumbnail}" alt="${product.title}" loading="lazy" class="max-h-28 object-contain" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1">
            <span class="text-xs font-extrabold text-blue-600 uppercase tracking-wider">${product.brand}</span>
            <span class="text-[10px] bg-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded">${product.offersCount} OFERTAS EN CDE</span>
            <span class="text-xs text-amber-500 font-bold">★ ${product.rating.toFixed(1)}</span>
          </div>
          <h3 class="font-bold text-slate-900 text-sm leading-snug mb-1.5" title="${product.title}">${product.title}</h3>
          <p class="text-xs text-slate-500 line-clamp-2 mb-2">${product.description}</p>
          <div class="text-xs text-slate-600">Comercio con mejor precio: <strong>${bestMerchant?.displayName || 'CDE'}</strong> (${bestMerchant?.shoppingMall || 'Centro'})</div>
        </div>
        <div class="flex-shrink-0 text-right sm:border-l sm:border-slate-100 sm:pl-5 space-y-2 w-full sm:w-auto">
          <div class="text-[11px] text-slate-500 font-semibold">Desde:</div>
          <div class="text-xl font-black text-slate-900 font-mono">US$ ${product.bestPriceUSD.toFixed(2)}</div>
          <div class="text-sm font-bold text-blue-700 font-mono">${formattedPrice}</div>
          <div class="flex items-center gap-2 pt-1">
            <button data-action="toggle-trip-item" data-product-id="${product.id}" class="text-xs px-2.5 py-1.5 rounded-lg border font-bold ${isInTrip ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200'}">
              ${isInTrip ? '✓ En Valija' : '➕ Valija'}
            </button>
            <button data-action="compare-stores" data-product-id="${product.id}" class="text-xs bg-slate-100 hover:bg-slate-200 font-bold text-slate-800 px-3 py-1.5 rounded-lg">
              Comparar
            </button>
            <a href="${safeOutUrl}" target="_blank" rel="noopener noreferrer" class="text-xs bg-[#d32f2f] hover:bg-red-700 text-white font-bold px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
              <span>Ir a ${bestMerchant?.displayName || 'Tienda'}</span>
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
            </a>
          </div>
        </div>
      </div>
    `;
  }

  return `
    <div class="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col justify-between overflow-hidden group">
      <div class="relative bg-white p-5 flex items-center justify-center aspect-square border-b border-slate-100">
        <img
          src="${product.thumbnail}"
          alt="${product.title}"
          loading="lazy"
          onerror="this.src='https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400&auto=format&fit=crop&q=80'"
          class="object-contain max-h-44 group-hover:scale-105 transition-transform duration-200"
        />
        <span class="absolute top-2.5 left-2.5 bg-amber-500 text-slate-950 text-[10px] font-black uppercase px-2 py-0.5 rounded shadow-sm">
          Mejor Precio
        </span>
        <div class="absolute top-2.5 right-2.5 bg-slate-50 px-2 py-0.5 rounded text-xs font-bold text-slate-700 flex items-center gap-1 border border-slate-200">
          <span class="text-amber-500">★</span> ${product.rating.toFixed(1)}
        </div>
      </div>

      <div class="p-4 flex-1 flex flex-col justify-between">
        <div>
          <div class="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span class="uppercase tracking-wider font-extrabold text-blue-600">${product.brand}</span>
            <span class="bg-blue-50 text-blue-700 font-bold px-1.5 py-0.5 rounded text-[10px]">${product.offersCount} tiendas</span>
          </div>

          <h3 class="font-bold text-slate-900 text-sm leading-snug line-clamp-2 mb-2 group-hover:text-blue-600 transition-colors" title="${product.title}">
            ${product.title}
          </h3>
        </div>

        <div>
          <div class="bg-slate-50 rounded-lg p-2.5 border border-slate-100 mb-3">
            <div class="text-[11px] text-slate-500 flex items-center justify-between">
              <span>Desde (${bestMerchant?.displayName || 'CDE'}):</span>
              <span class="font-mono text-slate-700 font-bold">US$ ${product.bestPriceUSD.toFixed(2)}</span>
            </div>
            <div class="text-xl font-black text-slate-900 font-mono mt-0.5">
              ${formattedPrice}
            </div>
          </div>

          <button
            data-action="toggle-trip-item"
            data-product-id="${product.id}"
            class="w-full text-center mb-2 font-bold text-xs py-1.5 px-2 rounded-lg transition-all ${
              isInTrip
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }"
          >
            <span>${isInTrip ? '✓ En mi Valija CDE' : '➕ Agregar a mi Valija'}</span>
          </button>

          <div class="grid grid-cols-2 gap-2">
            <button
              data-action="compare-stores"
              data-product-id="${product.id}"
              class="w-full text-center bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs py-2 px-2 rounded-lg transition-colors"
            >
              <span>Comparar</span>
            </button>

            <a
              href="${safeOutUrl}"
              target="_blank"
              rel="noopener noreferrer"
              class="w-full text-center bg-[#d32f2f] hover:bg-red-700 text-white font-bold text-xs py-2 px-2 rounded-lg shadow-sm transition-all flex items-center justify-center gap-1"
            >
              <span>Ir a Tienda</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderProductsSectionLoading() {
  const container = document.getElementById('products-grid-container');
  if (!container) return;

  container.innerHTML = `
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
      ${Array.from({ length: 6 })
        .map(
          () => `
        <div class="bg-white rounded-xl border border-slate-200 p-4 space-y-3 shadow-sm animate-pulse">
          <div class="bg-slate-200 rounded-lg aspect-square w-full"></div>
          <div class="h-4 bg-slate-200 rounded w-1/3"></div>
          <div class="h-5 bg-slate-200 rounded w-4/5"></div>
          <div class="h-10 bg-slate-100 rounded-lg"></div>
          <div class="grid grid-cols-2 gap-2">
            <div class="h-8 bg-slate-200 rounded-lg"></div>
            <div class="h-8 bg-slate-200 rounded-lg"></div>
          </div>
        </div>
      `
        )
        .join('')}
    </div>
  `;
}

function renderProductsGrid() {
  const container = document.getElementById('products-grid-container');
  const titleEl = document.getElementById('results-main-title');
  const breadcrumbEl = document.getElementById('results-breadcrumb-text');
  const activeFiltersStrip = document.getElementById('active-filters-strip');
  if (!container) return;

  const filteredProducts = getFilteredProducts();
  const activeCatObj = CATEGORIES_HIERARCHY.find((c) => c.id === state.selectedCategory);

  let queryDisplay = 'CATÁLOGO COMPLETO CIUDAD DEL ESTE';
  if (state.searchQuery.trim()) {
    queryDisplay = state.searchQuery.toUpperCase();
  } else if (state.selectedBrand !== 'all' && activeCatObj && activeCatObj.id !== 'all') {
    queryDisplay = `${activeCatObj.label.toUpperCase()} - ${state.selectedBrand.toUpperCase()}`;
  } else if (state.selectedBrand !== 'all') {
    queryDisplay = `MARCA ${state.selectedBrand.toUpperCase()}`;
  } else if (activeCatObj && activeCatObj.id !== 'all') {
    queryDisplay = activeCatObj.label.toUpperCase();
  }

  if (titleEl) {
    const isAll = state.selectedCategory === 'all' && state.selectedBrand === 'all' && !state.searchQuery.trim();
    const countDisplay = isAll ? '+65.000' : state.totalCatalogCount.toLocaleString('es-AR');
    titleEl.innerHTML = `
      <span class="text-slate-900">${queryDisplay}</span>
      <span class="text-[#0d47a1] font-semibold text-sm sm:text-base ml-2">(${countDisplay} Resultados)</span>
    `;
  }

  if (breadcrumbEl) {
    if (state.searchQuery.trim()) {
      breadcrumbEl.innerText = state.searchQuery.trim();
    } else if (state.selectedBrand !== 'all' && activeCatObj && activeCatObj.id !== 'all') {
      breadcrumbEl.innerText = `${activeCatObj.label} > ${state.selectedBrand}`;
    } else if (state.selectedBrand !== 'all') {
      breadcrumbEl.innerText = state.selectedBrand;
    } else if (activeCatObj && activeCatObj.id !== 'all') {
      breadcrumbEl.innerText = activeCatObj.label;
    } else {
      breadcrumbEl.innerText = 'Catálogo';
    }
  }

  if (activeFiltersStrip) {
    const filterTags: string[] = [];
    if (state.selectedCategory !== 'all' && activeCatObj) {
      filterTags.push(`
        <span class="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">
          <span>${activeCatObj.icon} ${activeCatObj.label}</span>
          <button data-remove-filter="category" class="hover:text-blue-950 font-black">✕</button>
        </span>
      `);
    }
    if (state.selectedBrand !== 'all') {
      filterTags.push(`
        <span class="inline-flex items-center gap-1.5 bg-slate-200 text-slate-800 text-xs font-bold px-3 py-1 rounded-full">
          <span>🏷️ ${state.selectedBrand}</span>
          <button data-remove-filter="brand" class="hover:text-black font-black">✕</button>
        </span>
      `);
    }
    if (state.selectedStore !== 'all') {
      filterTags.push(`
        <span class="inline-flex items-center gap-1.5 bg-slate-200 text-slate-800 text-xs font-bold px-3 py-1 rounded-full">
          <span>🏪 ${state.selectedStore}</span>
          <button data-remove-filter="store" class="hover:text-black font-black">✕</button>
        </span>
      `);
    }
    if (state.minPrice > 0 || state.maxPrice > 0) {
      filterTags.push(`
        <span class="inline-flex items-center gap-1.5 bg-slate-200 text-slate-800 text-xs font-bold px-3 py-1 rounded-full">
          <span>💵 $${state.minPrice} - $${state.maxPrice || '∞'} USD</span>
          <button data-remove-filter="price" class="hover:text-black font-black">✕</button>
        </span>
      `);
    }

    if (filterTags.length > 0) {
      activeFiltersStrip.innerHTML = `
        <div class="flex flex-wrap items-center gap-2 mb-4 p-2.5 bg-slate-50 rounded-xl border border-slate-200">
          <span class="text-xs text-slate-500 font-semibold">Filtros activos:</span>
          ${filterTags.join('')}
          <button id="clear-all-tags-btn" class="text-xs text-blue-600 hover:underline font-bold ml-auto">Limpiar Todo</button>
        </div>
      `;

      activeFiltersStrip.querySelector('[data-remove-filter="category"]')?.addEventListener('click', () => {
        selectCategory('all');
      });
      activeFiltersStrip.querySelector('[data-remove-filter="brand"]')?.addEventListener('click', () => {
        selectBrand('all');
      });
      activeFiltersStrip.querySelector('[data-remove-filter="store"]')?.addEventListener('click', () => {
        state.selectedStore = 'all';
        renderSidebarStores();
        renderProductsGrid();
      });
      activeFiltersStrip.querySelector('[data-remove-filter="price"]')?.addEventListener('click', () => {
        state.minPrice = 0;
        state.maxPrice = 0;
        const minEl = document.getElementById('min-price-input') as HTMLInputElement | null;
        const maxEl = document.getElementById('max-price-input') as HTMLInputElement | null;
        if (minEl) minEl.value = '';
        if (maxEl) maxEl.value = '';
        renderProductsGrid();
      });
      activeFiltersStrip.querySelector('#clear-all-tags-btn')?.addEventListener('click', resetAllFilters);
    } else {
      activeFiltersStrip.innerHTML = '';
    }
  }

  if (filteredProducts.length === 0) {
    container.innerHTML = `
      <div class="bg-white rounded-xl p-12 text-center border border-slate-200">
        <p class="text-slate-600 text-sm font-medium mb-3">No se encontraron productos para los filtros seleccionados.</p>
        <button id="clear-all-filters-btn" class="bg-[#0d47a1] hover:bg-blue-800 text-white text-xs font-bold px-4 py-2 rounded-lg">
          Limpiar Filtros y Ver Todos
        </button>
      </div>
    `;
    const clearBtn = document.getElementById('clear-all-filters-btn');
    if (clearBtn) clearBtn.addEventListener('click', resetAllFilters);
    return;
  }

  container.innerHTML = `
    <div class="${state.viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5' : 'space-y-4'}">
      ${filteredProducts.map((p) => renderProductCard(p)).join('')}
    </div>

    <!-- Infinite Scroll Sentinel -->
    <div id="infinite-scroll-trigger" class="mt-8 py-4 text-center">
      ${
        state.isLoadingMore
          ? '<div class="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 shadow-sm"><div class="w-4 h-4 border-2 border-[#0d47a1] border-t-transparent rounded-full animate-spin"></div> Obteniendo más productos en tiempo real desde Ciudad del Este...</div>'
          : state.hasMorePages
          ? '<button id="manual-load-more-btn" class="bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 font-bold text-xs py-2.5 px-5 rounded-lg shadow-sm">Cargar Más Resultados (+40)</button>'
          : '<div class="text-slate-400 text-xs font-medium">✓ Has explorado todo el catálogo disponible</div>'
      }
    </div>
  `;

  container.querySelectorAll('[data-action="toggle-trip-item"]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const prodId = (e.currentTarget as HTMLElement).getAttribute('data-product-id');
      const prod = state.products.find((p) => p.id === prodId);
      if (prod) addToTripCart(prod);
    });
  });

  container.querySelectorAll('[data-action="compare-stores"]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const prodId = (e.currentTarget as HTMLElement).getAttribute('data-product-id');
      const prod = state.products.find((p) => p.id === prodId) || null;
      if (prod) {
        state.activeProductForCompare = prod;
        state.compareModalActiveTab = 'prices';
        renderModals();
        loadLiveStoreOffersForProduct(prod);
      }
    });
  });

  const manualLoadBtn = document.getElementById('manual-load-more-btn');
  if (manualLoadBtn) {
    manualLoadBtn.addEventListener('click', () => {
      state.currentPage += 1;
      fetchLiveProducts(false);
    });
  }
}

function renderModals() {
  const modalContainer = document.getElementById('modals-container');
  if (!modalContainer) return;

  let html = '';

  // 1. Stores Modal
  if (state.storesModalOpen) {
    html += `
      <div id="stores-modal-overlay" class="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl max-w-4xl w-full p-6 shadow-2xl border border-slate-100 relative animate-in fade-in zoom-in-95 duration-150 max-h-[90vh] flex flex-col justify-between">
          <button id="close-stores-modal-btn" class="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>

          <div>
            <div class="flex items-center gap-3 pb-4 border-b border-slate-100 mb-5">
              <span class="text-2xl">🏪</span>
              <div>
                <h2 class="text-lg font-black text-slate-900">Tiendas Verificadas de Ciudad del Este</h2>
                <p class="text-xs text-slate-500">Comercios oficiales con catálogo en tiempo real y garantía en Paraguay</p>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5 max-h-[60vh] overflow-y-auto pr-1">
              ${Object.values(CDE_MERCHANTS).map((m) => `
                <div class="bg-slate-50 border border-slate-200 rounded-xl p-3.5 flex flex-col justify-between space-y-3 hover:border-blue-300 transition-colors">
                  <div>
                    <div class="flex items-center justify-between">
                      <h3 class="font-bold text-xs text-slate-900">${m.displayName}</h3>
                      <span class="text-amber-500 text-xs font-bold">★ ${m.rating}</span>
                    </div>
                    <div class="text-[11px] text-slate-600 mt-1">📍 ${m.shoppingMall} (${m.floor})</div>
                    <div class="text-[10px] text-slate-400 mt-0.5 truncate">${m.address}</div>
                  </div>
                  <button data-filter-store="${m.displayName}" class="w-full py-1.5 bg-[#0d47a1] hover:bg-blue-800 text-white font-bold text-xs rounded-lg transition-colors">
                    Ver Catálogo de Tienda
                  </button>
                </div>
              `).join('')}
            </div>
          </div>

          <div class="pt-4 border-t border-slate-100 mt-4 flex justify-end">
            <button id="close-stores-modal-bottom-btn" class="px-5 py-2 bg-slate-900 text-white font-bold text-xs rounded-xl">
              Cerrar Directorio
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // 2. Comprehensive Brands Directory Modal (1,896 Official CDE Brands)
  if (state.brandsModalOpen) {
    const alphabetLetters = [
      '★', '0-9', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K',
      'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'
    ];

    let displayedBrands: BrandItem[] = [];
    const searchQ = state.brandsModalSearchQuery.toLowerCase().trim();

    if (searchQ) {
      displayedBrands = ALL_CDE_BRANDS.filter((b) =>
        b.name.toLowerCase().includes(searchQ)
      );
    } else if (state.brandsModalActiveLetter === '★') {
      const featSet = new Set(FEATURED_BRANDS.map((b) => b.toLowerCase()));
      displayedBrands = ALL_CDE_BRANDS.filter((b) => featSet.has(b.name.toLowerCase()));
    } else if (state.brandsModalActiveLetter === '0-9') {
      displayedBrands = ALL_CDE_BRANDS.filter((b) => b.firstLetter === '0-9');
    } else {
      displayedBrands = ALL_CDE_BRANDS.filter(
        (b) => b.firstLetter === state.brandsModalActiveLetter
      );
    }

    html += `
      <div id="brands-modal-overlay" class="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
        <div class="bg-white rounded-2xl max-w-4xl w-full p-5 sm:p-6 shadow-2xl border border-slate-100 relative animate-in fade-in zoom-in-95 duration-150 max-h-[92vh] flex flex-col justify-between">
          <button id="close-brands-modal-btn" class="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>

          <div>
            <div class="flex items-center gap-3 pb-3 border-b border-slate-100 mb-4">
              <span class="text-2xl">🏷️</span>
              <div>
                <h2 class="text-base sm:text-lg font-black text-slate-900">Directorio Oficial de Marcas en Ciudad del Este</h2>
                <p class="text-xs text-slate-500">Más de 1.890 marcas verificadas de importación directa</p>
              </div>
            </div>

            <!-- Search input inside modal -->
            <div class="mb-3">
              <div class="relative">
                <input
                  id="brands-modal-search-input"
                  type="text"
                  placeholder="🔍 Buscar marca (ej: Apple, Calvin Klein, Stanley, DeWalt, Lattafa...)"
                  value="${state.brandsModalSearchQuery}"
                  class="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-2.5 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0d47a1]"
                />
              </div>
            </div>

            <!-- A-Z Alphabet Selector -->
            <div class="flex items-center gap-1 overflow-x-auto pb-2 mb-3 border-b border-slate-100 scrollbar-thin">
              ${alphabetLetters
                .map(
                  (letter) => `
                <button
                  data-brand-letter="${letter}"
                  class="px-2.5 py-1 rounded-lg text-xs font-bold transition-colors whitespace-nowrap ${
                    state.brandsModalActiveLetter === letter && !searchQ
                      ? 'bg-[#0d47a1] text-white shadow-sm'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }"
                >
                  ${letter === '★' ? '★ Destacadas' : letter}
                </button>
              `
                )
                .join('')}
            </div>

            <!-- Brands Chips Grid -->
            <div class="max-h-[50vh] overflow-y-auto pr-1 scrollbar-thin">
              <div class="text-[11px] font-semibold text-slate-400 mb-2">
                ${displayedBrands.length} marcas encontradas ${searchQ ? `para "${searchQ}"` : `en sección ${state.brandsModalActiveLetter}`}
              </div>
              <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                ${displayedBrands
                  .map(
                    (b) => `
                  <button
                    data-filter-brand="${b.name}"
                    class="text-left p-2.5 rounded-xl border border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-slate-800 hover:text-[#0d47a1] text-xs font-bold transition-all truncate flex items-center justify-between group ${
                      state.selectedBrand.toLowerCase() === b.name.toLowerCase() ? 'bg-blue-50 border-blue-400 text-[#0d47a1]' : 'bg-slate-50/70'
                    }"
                    title="${b.name}"
                  >
                    <span class="truncate">${b.name}</span>
                    <span class="text-[10px] text-slate-400 group-hover:text-blue-500 ml-1">↗</span>
                  </button>
                `
                  )
                  .join('')}
              </div>
            </div>
          </div>

          <div class="pt-3 border-t border-slate-100 mt-3 flex items-center justify-between">
            <span class="text-[11px] text-slate-400">Total: 1.896 marcas indexadas</span>
            <button id="close-brands-modal-bottom-btn" class="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-colors">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // 3. Comparison Modal
  if (state.activeProductForCompare && state.rates) {
    const product = state.activeProductForCompare;
    const jsonLd = generateProductJsonLd(
      product,
      state.selectedCurrency,
      state.rates[state.selectedCurrency]?.sell || 1.0
    );

    html += `
      <div id="compare-modal" class="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl max-w-3xl w-full p-6 shadow-2xl border border-slate-100 relative animate-in fade-in zoom-in-95 duration-150 max-h-[90vh] flex flex-col justify-between">
          <button id="close-compare-modal-btn" class="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>

          <div class="overflow-y-auto pr-1">
            <div class="flex items-start gap-4 mb-5 border-b border-slate-100 pb-4">
              <img src="${product.thumbnail}" alt="${product.title}" class="w-16 h-16 object-contain bg-slate-50 p-2 rounded-xl border border-slate-200" />
              <div>
                <div class="flex items-center gap-2">
                  <span class="text-xs font-bold uppercase text-blue-600 tracking-wider">${product.brand}</span>
                  <span class="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full">● Datos en vivo de CDE</span>
                </div>
                <h2 class="text-base sm:text-lg font-bold text-slate-900 leading-snug">${product.title}</h2>
                <p class="text-xs text-slate-500">Comparador de precios por tienda y ubicación en galerías de Ciudad del Este</p>
              </div>
            </div>

            <div class="flex items-center gap-2 border-b border-slate-200 pb-2 mb-4 text-xs font-bold">
              <button data-tab="prices" class="px-3 py-1.5 rounded-lg transition-colors ${state.compareModalActiveTab === 'prices' ? 'bg-[#0d47a1] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}">
                🏷️ Precios por Tienda (${product.offers.length})
              </button>
              <button data-tab="locations" class="px-3 py-1.5 rounded-lg transition-colors ${state.compareModalActiveTab === 'locations' ? 'bg-[#0d47a1] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}">
                📍 Galerías & Ubicación CDE
              </button>
              <button data-tab="payments" class="px-3 py-1.5 rounded-lg transition-colors ${state.compareModalActiveTab === 'payments' ? 'bg-[#0d47a1] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}">
                💳 Medios de Pago & Recargos
              </button>
            </div>

            ${
              state.compareModalActiveTab === 'prices'
                ? `
              <div class="overflow-x-auto mb-6">
                <table class="w-full text-left text-xs">
                  <thead>
                    <tr class="border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider text-[11px]">
                      <th class="pb-3 pl-2">Comercio CDE</th>
                      <th class="pb-3">Ubicación</th>
                      <th class="pb-3">Stock & Garantía</th>
                      <th class="pb-3">Precio USD</th>
                      <th class="pb-3">Precio ${CURRENCY_CONFIGS[state.selectedCurrency].shortName}</th>
                      <th class="pb-3 pr-2 text-right">Acción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100">
                    ${
                      state.compareOffersLoading
                        ? `<tr><td colspan="6" class="py-8 text-center text-slate-500"><div class="inline-flex items-center gap-2"><div class="w-4 h-4 border-2 border-[#0d47a1] border-t-transparent rounded-full animate-spin"></div> Consultando precios en vivo en comercios de Ciudad del Este...</div></td></tr>`
                        : product.offers
                            .map((offer: StoreOffer, index: number) => {
                              const merchant = CDE_MERCHANTS[offer.storeId];
                              const priceInCur = convertUsdToCurrency(offer.priceUSD, state.selectedCurrency, state.rates!);
                              const isLowest = index === 0;
                              const directOfferUrl = getDirectStoreProductUrl(offer.storeId, product.title, offer.productUrl);
                              const safeOutUrl = `/api/out?url=${encodeURIComponent(directOfferUrl)}&storeId=${offer.storeId}&prodId=${product.id}&title=${encodeURIComponent(product.title)}`;

                              return `
                            <tr class="hover:bg-slate-50/80 transition-colors ${isLowest ? 'bg-amber-50/40 font-medium' : ''}">
                              <td class="py-3 pl-2 flex items-center gap-2">
                                <div class="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-[10px] text-slate-700">
                                  ${offer.storeName.substring(0, 2).toUpperCase()}
                                </div>
                                <div>
                                  <div class="font-bold text-slate-900">${offer.storeName}</div>
                                  ${isLowest ? '<span class="text-[9px] font-extrabold text-amber-600 uppercase">★ Mejor Precio</span>' : ''}
                                </div>
                              </td>
                              <td class="py-3 text-slate-600">${merchant?.shoppingMall || 'CDE Centro'}</td>
                              <td class="py-3">
                                <span class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${offer.stockStatus === 'in_stock' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}">
                                  ${offer.stockStatus === 'in_stock' ? 'En Stock' : 'Stock Limitado'}
                                </span>
                                <span class="text-slate-400 block text-[10px] mt-0.5">${offer.warrantyMonths}m garantía</span>
                              </td>
                              <td class="py-3 font-mono font-bold text-slate-900">US$ ${offer.priceUSD.toFixed(2)}</td>
                              <td class="py-3 font-mono font-bold text-blue-700">${formatCurrencyAmount(priceInCur, state.selectedCurrency)}</td>
                              <td class="py-3 pr-2 text-right">
                                <a href="${safeOutUrl}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 bg-[#d32f2f] hover:bg-red-700 text-white font-semibold text-[11px] px-3 py-1.5 rounded-lg transition-colors">
                                  <span>Ir a ${merchant?.displayName || 'Tienda'}</span>
                                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                                </a>
                              </td>
                            </tr>
                          `;
                            })
                            .join('')
                    }
                  </tbody>
                </table>
              </div>
            `
                : state.compareModalActiveTab === 'locations'
                ? `
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                ${product.offers
                  .map((offer) => {
                    const merchant = CDE_MERCHANTS[offer.storeId];
                    return `
                    <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                      <div class="font-bold text-slate-900 flex items-center justify-between">
                        <span>${offer.storeName}</span>
                        <span class="text-amber-500 text-xs">★ ${merchant?.rating || '4.8'}</span>
                      </div>
                      <div class="text-xs text-slate-600"><strong>Galería/Mall:</strong> ${merchant?.shoppingMall || 'Shopping del Este'}</div>
                      <div class="text-xs text-slate-600"><strong>Dirección:</strong> ${merchant?.address || 'Av. San Blas, Ciudad del Este'}</div>
                      <div class="text-xs text-slate-600"><strong>Piso/Local:</strong> ${merchant?.floor || 'Planta Principal'}</div>
                    </div>
                  `;
                  })
                  .join('')}
              </div>
            `
                : `
              <div class="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6 space-y-4 text-xs">
                <h3 class="font-bold text-slate-900 text-sm">Opciones de Pago en Tiendas de Ciudad del Este</h3>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div class="bg-white p-3 rounded-lg border border-slate-200">
                    <div class="font-bold text-emerald-700 mb-1">💵 Dólares en Efectivo (USD)</div>
                    <p class="text-slate-600 text-[11px]">Medio de pago recomendado sin recargo comercial. Billetes franja azul.</p>
                  </div>
                  <div class="bg-white p-3 rounded-lg border border-slate-200">
                    <div class="font-bold text-blue-700 mb-1">⚡ Pix Brasilero (BRL)</div>
                    <p class="text-slate-600 text-[11px]">Aceptado en todos los comercios al tipo de cambio oficial del día.</p>
                  </div>
                  <div class="bg-white p-3 rounded-lg border border-slate-200">
                    <div class="font-bold text-amber-700 mb-1">💳 Tarjetas de Crédito / Débito</div>
                    <p class="text-slate-600 text-[11px]">Las tiendas en CDE aplican habitualmente 5% a 7% por POS bancario.</p>
                  </div>
                  <div class="bg-white p-3 rounded-lg border border-slate-200">
                    <div class="font-bold text-purple-700 mb-1">🇦🇷 Pesos Argentinos en Efectivo</div>
                    <p class="text-slate-600 text-[11px]">Aceptados en casas de cambio al Dólar Blue del día.</p>
                  </div>
                </div>
              </div>
            `
            }

            <div class="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
              <div class="text-xs text-blue-950">
                <strong class="block font-bold">¿Cuánto pagarías en Aduana ARCA por este producto?</strong>
                <span>Calcula la franquicia grupal o individual al cruzar la frontera o aeropuerto.</span>
              </div>
              <button id="modal-calculate-customs-btn" data-price="${product.bestPriceUSD}" class="bg-[#0d47a1] hover:bg-blue-800 text-white text-xs font-bold px-3.5 py-2 rounded-lg transition-all">
                Calcular Aduana
              </button>
            </div>
          </div>

          <script type="application/ld+json" id="product-schema-jsonld">${JSON.stringify(jsonLd, null, 2)}</script>
        </div>
      </div>
    `;
  }

  // 4. Customs Drawer
  if (state.customsDrawerOpen) {
    const bnaRate = state.rates?.ARS_OFICIAL.sell || 1510;
    const effectiveTotalUsd = Math.max(
      0,
      state.customsInput.itemsTotalUSD -
        (state.customsInput.exemptSmartphone ? 500 : 0) -
        (state.customsInput.exemptLaptop ? 800 : 0)
    );

    const result = calculateArcaCustomsDuty(
      {
        transportType: state.customsInput.transportType,
        adultsCount: state.customsInput.adultsCount,
        minorsUnder16Count: state.customsInput.minorsCount,
        itemsTotalUSD: effectiveTotalUsd,
        isDutyFreeAirportPurchase: false,
        dutyFreeSpentUSD: 0,
      },
      bnaRate
    );

    html += `
      <div id="customs-drawer" class="fixed inset-0 z-50 overflow-hidden bg-slate-900/60 backdrop-blur-sm flex justify-end animate-in fade-in duration-150">
        <div class="bg-white max-w-lg w-full h-full shadow-2xl p-6 overflow-y-auto flex flex-col justify-between animate-in slide-in-from-right duration-200">
          <div>
            <div class="flex items-center justify-between pb-4 border-b border-slate-200 mb-6">
              <div class="flex items-center gap-2.5">
                <div class="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold">🇦🇷</div>
                <div>
                  <h2 class="text-base font-extrabold text-slate-900">Calculadora Aduana ARCA / AFIP</h2>
                  <p class="text-xs text-slate-500">Régimen de Equipaje de Viajeros (Frontera y Aeropuerto)</p>
                </div>
              </div>
              <button id="close-customs-drawer-btn" class="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>

            <div class="space-y-4 text-xs">
              <div>
                <label class="block font-bold text-slate-700 mb-1.5">Vía de Ingreso a Argentina</label>
                <div class="grid grid-cols-2 gap-2">
                  <button type="button" data-customs-type="terrestrial_river" class="py-2.5 px-3 rounded-xl border text-center font-semibold transition-all ${state.customsInput.transportType === 'terrestrial_river' ? 'border-blue-600 bg-blue-50 text-blue-700 ring-2 ring-blue-600/20' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}">
                    🚗 Terrestre / Puente ($300 USD)
                  </button>
                  <button type="button" data-customs-type="air_maritime" class="py-2.5 px-3 rounded-xl border text-center font-semibold transition-all ${state.customsInput.transportType === 'air_maritime' ? 'border-blue-600 bg-blue-50 text-blue-700 ring-2 ring-blue-600/20' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}">
                    ✈️ Aéreo / Aeropuerto ($500 USD)
                  </button>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block font-bold text-slate-700 mb-1">Adultos (>16)</label>
                  <div class="flex items-center border border-slate-200 rounded-xl overflow-hidden">
                    <button type="button" id="adults-minus" class="px-3 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-slate-700">-</button>
                    <input type="number" min="1" max="10" value="${state.customsInput.adultsCount}" class="w-full text-center py-2 text-xs font-bold border-none focus:ring-0" readonly />
                    <button type="button" id="adults-plus" class="px-3 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-slate-700">+</button>
                  </div>
                </div>

                <div>
                  <label class="block font-bold text-slate-700 mb-1">Menores (<16)</label>
                  <div class="flex items-center border border-slate-200 rounded-xl overflow-hidden">
                    <button type="button" id="minors-minus" class="px-3 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-slate-700">-</button>
                    <input type="number" min="0" max="10" value="${state.customsInput.minorsCount}" class="w-full text-center py-2 text-xs font-bold border-none focus:ring-0" readonly />
                    <button type="button" id="minors-plus" class="px-3 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-slate-700">+</button>
                  </div>
                </div>
              </div>

              <div>
                <label for="customs-total-usd-input" class="block font-bold text-slate-700 mb-1">Monto Total de Compras (USD)</label>
                <div class="relative">
                  <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-bold">US$</span>
                  <input id="customs-total-usd-input" type="number" min="0" step="10" value="${state.customsInput.itemsTotalUSD}" class="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-base font-mono font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-600 focus:outline-none" />
                </div>
              </div>

              <div class="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                <div class="font-bold text-slate-900 text-xs flex items-center gap-1">
                  <span>🛡️</span> Exenciones Personales (RG 4331):
                </div>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" id="exempt-phone-toggle" ${state.customsInput.exemptSmartphone ? 'checked' : ''} class="w-4 h-4 text-blue-600 rounded" />
                  <span class="text-slate-700 font-medium">1 Teléfono Celular (Uso personal exento)</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" id="exempt-laptop-toggle" ${state.customsInput.exemptLaptop ? 'checked' : ''} class="w-4 h-4 text-blue-600 rounded" />
                  <span class="text-slate-700 font-medium">1 Notebook o Tablet (Uso personal exento)</span>
                </label>
              </div>
            </div>

            <div class="mt-6 bg-slate-900 text-white rounded-2xl p-5 shadow-lg space-y-3">
              <div class="flex items-center justify-between text-xs text-slate-400 border-b border-slate-800 pb-2">
                <span>Franquicia Total Grupal:</span>
                <span class="font-mono font-bold text-white">US$ ${result.totalFranchiseUSD.toFixed(2)}</span>
              </div>
              <div class="flex items-center justify-between text-xs text-slate-400 border-b border-slate-800 pb-2">
                <span>Excedente Gravado:</span>
                <span class="font-mono font-bold ${result.taxableExcessUSD > 0 ? 'text-amber-400' : 'text-emerald-400'}">US$ ${result.taxableExcessUSD.toFixed(2)}</span>
              </div>
              <div class="pt-1">
                <div class="text-xs text-slate-400 font-semibold mb-1">Arancel a pagar en Aduana (50% sobre exceso):</div>
                <div class="text-2xl font-black font-mono text-emerald-400">${result.isExempt ? 'US$ 0.00 (EXENTO)' : `US$ ${result.dutyToPayUSD.toFixed(2)}`}</div>
                ${!result.isExempt ? `<div class="text-xs text-slate-300 font-mono mt-1">En Pesos: <strong class="text-white">$${result.dutyToPayARS.toLocaleString('es-AR')} ARS</strong> (BNA: $${result.officialRateBNA.toFixed(2)})</div>` : ''}
              </div>
            </div>
          </div>

          <div class="pt-4 border-t border-slate-200">
            <button id="close-customs-drawer-bottom-btn" class="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all">
              Entendido / Cerrar
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // 5. Trip Cart Drawer
  if (state.tripCartOpen) {
    const totalUsd = getTripCartTotalUsd();
    const bnaRate = state.rates?.ARS_OFICIAL.sell || 1510;
    const customs = calculateArcaCustomsDuty(
      {
        transportType: state.customsInput.transportType,
        adultsCount: state.customsInput.adultsCount,
        minorsUnder16Count: state.customsInput.minorsCount,
        itemsTotalUSD: totalUsd,
        isDutyFreeAirportPurchase: false,
        dutyFreeSpentUSD: 0,
      },
      bnaRate
    );

    html += `
      <div id="trip-cart-drawer" class="fixed inset-0 z-50 overflow-hidden bg-slate-900/60 backdrop-blur-sm flex justify-end animate-in fade-in duration-150">
        <div class="bg-white max-w-lg w-full h-full shadow-2xl p-6 overflow-y-auto flex flex-col justify-between animate-in slide-in-from-right duration-200">
          <div>
            <div class="flex items-center justify-between pb-4 border-b border-slate-200 mb-5">
              <div class="flex items-center gap-2">
                <span class="text-2xl">🧳</span>
                <div>
                  <h2 class="text-base font-extrabold text-slate-900">Mi Valija de Viaje a CDE</h2>
                  <p class="text-xs text-slate-500">${state.tripCart.length} productos seleccionados</p>
                </div>
              </div>
              <button id="close-trip-cart-btn" class="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>

            ${
              state.tripCart.length === 0
                ? `<div class="py-12 text-center text-slate-500 text-xs"><p class="mb-2">No agregaste productos a tu valija todavía.</p></div>`
                : `
              <div class="space-y-3 mb-6">
                ${state.tripCart
                  .map(
                    (item) => `
                  <div class="flex items-center justify-between bg-slate-50 p-3 rounded-xl border border-slate-200 text-xs">
                    <div class="flex items-center gap-3">
                      <img src="${item.product.thumbnail}" class="w-10 h-10 object-contain rounded-lg bg-white p-1" />
                      <div>
                        <div class="font-bold text-slate-900 line-clamp-1 max-w-[200px]">${item.product.title}</div>
                        <div class="text-slate-500 font-mono">US$ ${item.product.bestPriceUSD.toFixed(2)} x ${item.quantity}</div>
                      </div>
                    </div>
                    <button data-remove-trip-id="${item.product.id}" class="text-red-500 hover:text-red-700 font-bold text-xs p-1">✕</button>
                  </div>
                `
                  )
                  .join('')}
              </div>

              <div class="bg-slate-900 text-white rounded-2xl p-5 shadow-lg space-y-3 mb-4">
                <div class="flex items-center justify-between text-xs text-slate-400 border-b border-slate-800 pb-2">
                  <span>Total Compras CDE:</span>
                  <span class="font-mono font-black text-white text-base">US$ ${totalUsd.toFixed(2)}</span>
                </div>
                <div class="flex items-center justify-between text-xs text-slate-400 border-b border-slate-800 pb-2">
                  <span>Franquicia (${state.customsInput.transportType === 'terrestrial_river' ? 'Terrestre' : 'Aérea'}):</span>
                  <span class="font-mono font-bold text-white">US$ ${customs.totalFranchiseUSD.toFixed(2)}</span>
                </div>
                <div class="flex items-center justify-between text-xs text-slate-400 border-b border-slate-800 pb-2">
                  <span>Excedente Gravado:</span>
                  <span class="font-mono font-bold ${customs.taxableExcessUSD > 0 ? 'text-amber-400' : 'text-emerald-400'}">US$ ${customs.taxableExcessUSD.toFixed(2)}</span>
                </div>
                <div>
                  <div class="text-xs text-slate-400 font-semibold mb-1">Arancel Aduana ARCA (50%):</div>
                  <div class="text-2xl font-black font-mono text-emerald-400">${customs.isExempt ? 'US$ 0.00 (EXENTO)' : `US$ ${customs.dutyToPayUSD.toFixed(2)}`}</div>
                  ${!customs.isExempt ? `<div class="text-xs text-slate-300 font-mono mt-1">En Pesos: <strong>$${customs.dutyToPayARS.toLocaleString('es-AR')} ARS</strong></div>` : ''}
                </div>
              </div>

              <a
                href="https://wa.me/?text=${generateWhatsAppTripMessage()}"
                target="_blank"
                rel="noopener noreferrer"
                class="w-full mb-3 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl flex items-center justify-center gap-2 transition-all shadow-sm"
              >
                <span>💬 Compartir mi lista por WhatsApp</span>
              </a>
            `
            }
          </div>

          <div class="pt-4 border-t border-slate-200">
            <button id="close-trip-cart-bottom-btn" class="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl">
              Cerrar Resumen
            </button>
          </div>
        </div>
      </div>
    `;
  }

  modalContainer.innerHTML = html;
  attachModalEventListeners();
}

function attachModalEventListeners() {
  const closeCompareBtn = document.getElementById('close-compare-modal-btn');
  if (closeCompareBtn) {
    closeCompareBtn.addEventListener('click', () => {
      state.activeProductForCompare = null;
      renderModals();
    });
  }

  document.querySelectorAll('[data-tab]').forEach((tabBtn) => {
    tabBtn.addEventListener('click', (e) => {
      state.compareModalActiveTab = (e.currentTarget as HTMLElement).getAttribute('data-tab') as
        | 'prices'
        | 'locations'
        | 'payments';
      renderModals();
    });
  });

  const modalCalcCustomsBtn = document.getElementById('modal-calculate-customs-btn');
  if (modalCalcCustomsBtn) {
    modalCalcCustomsBtn.addEventListener('click', (e) => {
      const price = Number((e.currentTarget as HTMLElement).getAttribute('data-price') || 900);
      state.customsInput.itemsTotalUSD = price;
      state.activeProductForCompare = null;
      state.customsDrawerOpen = true;
      renderModals();
    });
  }

  const closeStoresModalBtn = document.getElementById('close-stores-modal-btn');
  const closeStoresModalBottomBtn = document.getElementById('close-stores-modal-bottom-btn');
  [closeStoresModalBtn, closeStoresModalBottomBtn].forEach((btn) => {
    if (btn) {
      btn.addEventListener('click', () => {
        state.storesModalOpen = false;
        renderModals();
      });
    }
  });

  document.querySelectorAll('[data-filter-store]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const storeName = (e.currentTarget as HTMLElement).getAttribute('data-filter-store') || 'all';
      state.selectedStore = storeName;
      state.storesModalOpen = false;
      renderModals();
      renderSidebarStores();
      renderProductsGrid();
    });
  });

  const closeBrandsModalBtn = document.getElementById('close-brands-modal-btn');
  const closeBrandsModalBottomBtn = document.getElementById('close-brands-modal-bottom-btn');
  [closeBrandsModalBtn, closeBrandsModalBottomBtn].forEach((btn) => {
    if (btn) {
      btn.addEventListener('click', () => {
        state.brandsModalOpen = false;
        renderModals();
      });
    }
  });

  const brandsSearchInput = document.getElementById('brands-modal-search-input') as HTMLInputElement | null;
  if (brandsSearchInput) {
    brandsSearchInput.addEventListener('input', (e) => {
      state.brandsModalSearchQuery = (e.target as HTMLInputElement).value;
      renderModals();
      const nextInput = document.getElementById('brands-modal-search-input') as HTMLInputElement | null;
      if (nextInput) {
        nextInput.focus();
        nextInput.selectionStart = nextInput.selectionEnd = nextInput.value.length;
      }
    });
  }

  document.querySelectorAll('[data-brand-letter]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const letter = (e.currentTarget as HTMLElement).getAttribute('data-brand-letter') || '★';
      state.brandsModalActiveLetter = letter;
      state.brandsModalSearchQuery = '';
      renderModals();
    });
  });

  document.querySelectorAll('[data-filter-brand]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const brand = (e.currentTarget as HTMLElement).getAttribute('data-filter-brand') || 'all';
      selectBrand(brand);
    });
  });

  const closeCustomsBtn = document.getElementById('close-customs-drawer-btn');
  const closeCustomsBottomBtn = document.getElementById('close-customs-drawer-bottom-btn');
  [closeCustomsBtn, closeCustomsBottomBtn].forEach((btn) => {
    if (btn) {
      btn.addEventListener('click', () => {
        state.customsDrawerOpen = false;
        renderModals();
      });
    }
  });

  const closeTripBtn = document.getElementById('close-trip-cart-btn');
  const closeTripBottomBtn = document.getElementById('close-trip-cart-bottom-btn');
  [closeTripBtn, closeTripBottomBtn].forEach((btn) => {
    if (btn) {
      btn.addEventListener('click', () => {
        state.tripCartOpen = false;
        renderModals();
      });
    }
  });

  document.querySelectorAll('[data-remove-trip-id]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const prodId = (e.currentTarget as HTMLElement).getAttribute('data-remove-trip-id');
      if (prodId) removeFromTripCart(prodId);
    });
  });

  document.querySelectorAll('[data-customs-type]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      state.customsInput.transportType = (e.currentTarget as HTMLElement).getAttribute('data-customs-type') as BorderTransportType;
      renderModals();
    });
  });

  const adultsMinus = document.getElementById('adults-minus');
  const adultsPlus = document.getElementById('adults-plus');
  if (adultsMinus) {
    adultsMinus.addEventListener('click', () => {
      state.customsInput.adultsCount = Math.max(1, state.customsInput.adultsCount - 1);
      renderModals();
    });
  }
  if (adultsPlus) {
    adultsPlus.addEventListener('click', () => {
      state.customsInput.adultsCount = Math.min(10, state.customsInput.adultsCount + 1);
      renderModals();
    });
  }

  const minorsMinus = document.getElementById('minors-minus');
  const minorsPlus = document.getElementById('minors-plus');
  if (minorsMinus) {
    minorsMinus.addEventListener('click', () => {
      state.customsInput.minorsCount = Math.max(0, state.customsInput.minorsCount - 1);
      renderModals();
    });
  }
  if (minorsPlus) {
    minorsPlus.addEventListener('click', () => {
      state.customsInput.minorsCount = Math.min(10, state.customsInput.minorsCount + 1);
      renderModals();
    });
  }

  const customsTotalUsdInput = document.getElementById('customs-total-usd-input') as HTMLInputElement | null;
  if (customsTotalUsdInput) {
    customsTotalUsdInput.addEventListener('input', (e) => {
      state.customsInput.itemsTotalUSD = Number((e.target as HTMLInputElement).value) || 0;
      renderModals();
      const el = document.getElementById('customs-total-usd-input') as HTMLInputElement | null;
      if (el) el.focus();
    });
  }

  const exemptPhoneToggle = document.getElementById('exempt-phone-toggle') as HTMLInputElement | null;
  if (exemptPhoneToggle) {
    exemptPhoneToggle.addEventListener('change', (e) => {
      state.customsInput.exemptSmartphone = (e.target as HTMLInputElement).checked;
      renderModals();
    });
  }

  const exemptLaptopToggle = document.getElementById('exempt-laptop-toggle') as HTMLInputElement | null;
  if (exemptLaptopToggle) {
    exemptLaptopToggle.addEventListener('change', (e) => {
      state.customsInput.exemptLaptop = (e.target as HTMLInputElement).checked;
      renderModals();
    });
  }
}

function resetAllFilters() {
  state.searchQuery = '';
  state.selectedCategory = 'all';
  state.selectedBrand = 'all';
  state.selectedStore = 'all';
  state.minPrice = 0;
  state.maxPrice = 0;
  state.inStockOnly = false;
  state.sortBy = 'relevance';

  const searchInput = document.getElementById('main-search-input') as HTMLInputElement | null;
  if (searchInput) searchInput.value = '';

  const catSelect = document.getElementById('category-sidebar-select') as HTMLSelectElement | null;
  if (catSelect) catSelect.value = 'all';

  const brandSelect = document.getElementById('brand-select') as HTMLSelectElement | null;
  if (brandSelect) brandSelect.value = 'all';

  const minPriceInput = document.getElementById('min-price-input') as HTMLInputElement | null;
  if (minPriceInput) minPriceInput.value = '';

  const maxPriceInput = document.getElementById('max-price-input') as HTMLInputElement | null;
  if (maxPriceInput) maxPriceInput.value = '';

  const sortSelect = document.getElementById('sort-select') as HTMLSelectElement | null;
  if (sortSelect) sortSelect.value = 'relevance';

  syncUrlAndSeo(true);
  fetchLiveProducts(true);
}

// ==========================================
// MAIN SHELL RENDER FUNCTION (MOUNTED ONCE)
// ==========================================
export function initAppLayout() {
  const app = document.getElementById('app');
  if (!app) return;

  const allCurrencies = Object.values(CURRENCY_CONFIGS);

  app.innerHTML = `
    <!-- 1. Top Bar -->
    <div class="bg-[#0b3273] text-slate-300 text-xs py-1.5 px-4 border-b border-blue-950">
      <div id="rates-ticker-container"></div>
    </div>

    <!-- 2. Main White Header -->
    <header class="bg-white border-b border-slate-200 py-3.5 px-4 sm:px-6 lg:px-8 sticky top-0 z-40 shadow-sm">
      <div class="max-w-7xl mx-auto flex items-center justify-between gap-4 sm:gap-8">
        <!-- Logo -->
        <a href="/" id="header-logo-link" class="flex items-center gap-2.5 focus:outline-none flex-shrink-0 cursor-pointer">
          <div class="w-9 h-9 rounded-full bg-[#d32f2f] flex items-center justify-center text-white shadow-md">
            <svg class="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
          </div>
          <div>
            <div class="font-black text-xl text-slate-900 tracking-tight leading-none">
              Compras<span class="text-[#0d47a1]">Paraguay</span>
            </div>
            <p class="text-[10px] text-slate-500 font-semibold leading-tight">Precios en Vivo de Ciudad del Este</p>
          </div>
        </a>

        <!-- Large Search Bar with Red Button -->
        <form id="search-form" class="flex-1 max-w-2xl relative">
          <div class="flex items-center rounded-lg border-2 border-slate-300 focus-within:border-[#0d47a1] overflow-hidden bg-white shadow-inner">
            <input
              id="main-search-input"
              type="text"
              autocomplete="off"
              spellcheck="false"
              placeholder="Buscar productos, marcas, modelos (ej: Calvin Klein, iPhone 17, RTX 4070...)"
              class="w-full px-4 py-2.5 text-sm text-slate-900 focus:outline-none placeholder:text-slate-400"
            />
            <button
              type="submit"
              class="bg-[#d32f2f] hover:bg-red-700 text-white px-5 py-2.5 flex items-center justify-center transition-colors"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
            </button>
          </div>
        </form>

        <!-- Right Header Actions -->
        <div class="flex items-center gap-3">
          <!-- Currency Selector -->
          <div class="relative">
            <select id="currency-selector" class="appearance-none bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-800 text-xs font-bold py-2 pl-3 pr-7 rounded-lg cursor-pointer focus:outline-none">
              ${allCurrencies.map((c) => `<option value="${c.code}" ${c.code === state.selectedCurrency ? 'selected' : ''}>${c.flag} ${c.shortName}</option>`).join('')}
            </select>
          </div>

          <!-- Trip Cart / Valija Button -->
          <button id="open-trip-cart-btn" class="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold px-3 py-2 rounded-lg transition-colors relative">
            <span>🧳</span>
            <span class="hidden md:inline">Mi Valija</span>
            <span id="trip-cart-count" style="display: none;" class="bg-[#d32f2f] text-white text-[10px] font-black px-1.5 py-0.2 rounded-full"></span>
          </button>

          <!-- Customs Calculator Button -->
          <button id="open-customs-btn" class="flex items-center gap-1 bg-[#0d47a1] hover:bg-blue-800 text-white text-xs font-bold px-3.5 py-2 rounded-lg shadow-sm transition-colors">
            <span>🇦🇷 Aduana</span>
          </button>
        </div>
      </div>
    </header>

    <!-- 3. Primary Royal Blue Navigation Ribbon with Integrated Categories Megamenu -->
    <nav class="bg-[#0d47a1] text-white text-xs font-bold sticky top-[69px] z-30 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <div class="flex items-center">
          <!-- Integrated Categories Dropdown Wrapper -->
          <div class="relative" id="categories-dropdown-wrapper">
            <button id="categories-menu-btn" class="flex items-center gap-2 px-4 py-3.5 bg-blue-950/40 hover:bg-blue-950/70 transition-colors uppercase tracking-wider text-amber-300">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
              <span>Categorías</span>
              <svg class="w-3.5 h-3.5 ml-0.5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
            </button>

            <!-- Integrated Desktop & Mobile Megamenu Panel -->
            <div id="categories-megamenu-panel" class="hidden absolute top-full left-0 w-[720px] max-w-[92vw] bg-white text-slate-800 rounded-b-2xl shadow-2xl border border-slate-200 p-5 z-50 animate-in fade-in zoom-in-95 duration-150">
              <div class="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                <div class="font-black text-sm text-slate-900 flex items-center gap-2">
                  <span class="text-base text-[#0d47a1]">☰</span> Departamentos en Ciudad del Este
                </div>
                <span class="text-[11px] text-slate-400 font-medium">16 Categorías Oficiales</span>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 max-h-[65vh] overflow-y-auto pr-1 scrollbar-thin">
                ${CATEGORIES_HIERARCHY.map((cat) => `
                  <button data-nav-category="${cat.id}" class="text-left p-2.5 rounded-xl hover:bg-blue-50 transition-colors flex items-start gap-2.5 border border-transparent hover:border-blue-200 group ${state.selectedCategory === cat.id ? 'bg-blue-50 border-blue-200 font-bold' : ''}">
                    <span class="text-xl p-1.5 bg-slate-100 rounded-lg group-hover:bg-blue-100 transition-colors">${cat.icon}</span>
                    <div class="min-w-0 flex-1">
                      <div class="text-xs font-bold text-slate-900 group-hover:text-[#0d47a1] transition-colors leading-snug">${cat.label}</div>
                      <div class="text-[10px] text-slate-500 line-clamp-1 mt-0.5">${cat.desc}</div>
                    </div>
                  </button>
                `).join('')}
              </div>
            </div>
          </div>

          <button id="nav-btn-stores" class="px-4 py-3.5 hover:bg-blue-800 transition-colors uppercase tracking-wider whitespace-nowrap">
            🏪 Tiendas
          </button>
          <button id="nav-btn-brands" class="px-4 py-3.5 hover:bg-blue-800 transition-colors uppercase tracking-wider whitespace-nowrap">
            🏷️ Marcas
          </button>
          <button id="nav-btn-customs" class="px-4 py-3.5 hover:bg-blue-800 transition-colors uppercase tracking-wider whitespace-nowrap">
            ✈️ Turismo & Aduana
          </button>
          <button id="nav-btn-discounts" class="px-4 py-3.5 hover:bg-blue-800 transition-colors uppercase tracking-wider whitespace-nowrap text-amber-300">
            % Descuentos
          </button>
        </div>

        <div class="hidden lg:flex items-center text-[11px] text-blue-200">
          <span>Compras Paraguai Comparador de Precios Oficial</span>
        </div>
      </div>
    </nav>

    <!-- 4. Main Body Container -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <!-- Breadcrumb & Top Title Area -->
      <div class="mb-4">
        <div class="text-xs text-slate-500 mb-2">
          <span>Inicio</span> &gt; <span id="results-breadcrumb-text" class="text-slate-800 font-semibold">Catálogo</span>
        </div>

        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-3">
          <h1 id="results-main-title" class="text-xl sm:text-2xl font-black tracking-tight uppercase flex items-center">
            <span>CATÁLOGO COMPLETO CIUDAD DEL ESTE</span>
            <span class="text-[#0d47a1] font-semibold text-sm sm:text-base ml-2">(+65.000 Resultados)</span>
          </h1>

          <!-- View Mode & Sort Dropdown -->
          <div class="flex items-center gap-3">
            <div class="flex items-center bg-white border border-slate-200 rounded-lg p-0.5">
              <button id="view-grid-btn" title="Vista en Cuadrícula" class="p-1.5 rounded ${state.viewMode === 'grid' ? 'bg-slate-100 text-blue-700' : 'text-slate-400 hover:text-slate-700'}">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M3 3h8v8H3zm10 0h8v8h-8zM3 13h8v8H3zm10 0h8v8h-8z"/></svg>
              </button>
              <button id="view-list-btn" title="Vista en Lista" class="p-1.5 rounded ${state.viewMode === 'list' ? 'bg-slate-100 text-blue-700' : 'text-slate-400 hover:text-slate-700'}">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
              </button>
            </div>

            <div class="flex items-center gap-1.5 text-xs text-slate-600 font-semibold">
              <span>Ordenar por:</span>
              <select id="sort-select" class="bg-white border border-slate-200 text-xs rounded-lg p-1.5 font-medium focus:ring-1 focus:ring-[#0d47a1]">
                <option value="relevance">Relevancia</option>
                <option value="price_asc">Menor Precio</option>
                <option value="price_desc">Mayor Precio</option>
                <option value="offers_desc">Más Ofertas</option>
                <option value="rating_desc">Mejor Calificados</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Filter Tags Strip -->
      <div id="active-filters-strip"></div>

      <!-- Dynamic Suggestions Container -->
      <div id="suggestions-container"></div>

      <!-- Main 2-Column Layout (Sidebar + Products) -->
      <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <!-- 5. Sidebar Filters -->
        <aside class="space-y-5 bg-white p-4 rounded-xl border border-slate-200 shadow-sm h-fit">
          <div class="flex items-center justify-between border-b border-blue-600 pb-2">
            <h2 class="font-extrabold text-sm text-[#0d47a1] uppercase tracking-wider">Filtros</h2>
            <button id="reset-filters-btn" class="text-xs text-blue-600 hover:underline font-semibold">Limpiar</button>
          </div>

          <!-- Category Filter in Sidebar -->
          <div>
            <div class="font-bold text-xs text-slate-800 uppercase tracking-wider mb-2 border-b border-slate-100 pb-1">
              Categoría
            </div>
            <select id="category-sidebar-select" class="w-full bg-slate-50 border border-slate-200 text-xs rounded-lg p-2 font-medium">
              ${CATEGORIES_HIERARCHY.map((c) => `<option value="${c.id}" ${state.selectedCategory === c.id ? 'selected' : ''}>${c.icon} ${c.label}</option>`).join('')}
            </select>
          </div>

          <!-- Stores Filter (Lojas) -->
          <div>
            <div class="font-bold text-xs text-slate-800 uppercase tracking-wider mb-2 border-b border-slate-100 pb-1">
              Tiendas (Lojas)
            </div>
            <div id="stores-checkbox-list" class="space-y-1 max-h-52 overflow-y-auto scrollbar-thin pr-1">
              <!-- Rendered Dynamically -->
            </div>
          </div>

          <!-- Brands Filter with Direct Modal Jump -->
          <div>
            <div class="flex items-center justify-between font-bold text-xs text-slate-800 uppercase tracking-wider mb-2 border-b border-slate-100 pb-1">
              <span>Marcas</span>
              <button id="sidebar-open-brands-btn" type="button" class="text-[11px] text-blue-600 hover:text-blue-800 font-bold lowercase">Ver todas (+1.890) →</button>
            </div>
            <select id="brand-select" class="w-full bg-slate-50 border border-slate-200 text-xs rounded-lg p-2 font-medium">
              <!-- Rendered Dynamically -->
            </select>
          </div>

          <!-- Price Filter (Min / Max with Red OK Button) -->
          <div>
            <div class="font-bold text-xs text-slate-800 uppercase tracking-wider mb-2 border-b border-slate-100 pb-1">
              Precio (USD)
            </div>
            <div class="flex items-center gap-1.5">
              <input id="min-price-input" type="number" placeholder="Min" class="w-full bg-slate-50 border border-slate-200 text-xs rounded-lg p-2 font-mono" />
              <input id="max-price-input" type="number" placeholder="Max" class="w-full bg-slate-50 border border-slate-200 text-xs rounded-lg p-2 font-mono" />
              <button id="apply-price-btn" class="bg-[#d32f2f] hover:bg-red-700 text-white font-bold text-xs px-3 py-2 rounded-lg transition-colors">
                Ok
              </button>
            </div>
          </div>

          <!-- In Stock Switch -->
          <div class="flex items-center justify-between pt-2 border-t border-slate-100">
            <span class="text-xs font-semibold text-slate-700">Solo con Stock Inmediato</span>
            <input type="checkbox" id="instock-toggle" class="w-4 h-4 text-blue-600 rounded border-slate-300 cursor-pointer" />
          </div>
        </aside>

        <!-- 6. Products Grid Section -->
        <section class="lg:col-span-3">
          <div id="products-grid-container"></div>
        </section>
      </div>
    </main>

    <div id="modals-container"></div>

    <!-- 7. Global Original Footer -->
    <footer class="bg-[#0b3273] text-slate-300 text-xs py-10 mt-16 border-t border-blue-950">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <div class="text-white font-black text-base mb-2">Compras Paraguay</div>
          <p class="text-slate-400 leading-relaxed">
            Plataforma independiente de comparación de precios en Ciudad del Este (Paraguay) para compradores de Argentina y el Mercosur.
          </p>
        </div>
        <div>
          <h3 class="text-white font-bold mb-2 uppercase tracking-wider">Tiendas Verificadas</h3>
          <ul class="space-y-1 text-slate-300">
            <li>Shopping China</li>
            <li>Cellshop Importados</li>
            <li>Casa Nissei</li>
            <li>Mega Eletrônicos</li>
            <li>Madrid Center</li>
            <li>Atacado Games</li>
          </ul>
        </div>
        <div>
          <h3 class="text-white font-bold mb-2 uppercase tracking-wider">Aduana & Normativa</h3>
          <ul class="space-y-1 text-slate-300">
            <li>Franquicia Terrestre: $300 USD</li>
            <li>Franquicia Aérea: $500 USD</li>
            <li>Régimen de Equipaje RG 4331</li>
            <li>Alícuota arancelaria: 50% sobre exceso</li>
          </ul>
        </div>
        <div>
          <h3 class="text-white font-bold mb-2 uppercase tracking-wider">Infraestructura Edge</h3>
          <p class="text-slate-400 leading-relaxed mb-2">
            Desplegado globalmente en Cloudflare Pages & Workers con extracción en tiempo real.
          </p>
          <span class="inline-flex items-center gap-1 text-emerald-400 font-mono text-[11px]">
            ● Stream Bridge Active
          </span>
        </div>
      </div>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 mt-6 border-t border-blue-950 text-center text-slate-400 text-[11px]">
        © ${new Date().getFullYear()} ComprasParaguay.com.ar — Datos en vivo de comercios de Ciudad del Este.
      </div>
    </footer>
  `;

  attachStaticEventListeners();
}

function attachStaticEventListeners() {
  const searchForm = document.getElementById('search-form');
  const searchInput = document.getElementById('main-search-input') as HTMLInputElement | null;

  if (searchForm) {
    searchForm.addEventListener('submit', (e) => {
      e.preventDefault();
      if (searchInput) {
        state.searchQuery = searchInput.value.trim();
        state.selectedCategory = 'all';
        state.selectedBrand = 'all';
        const catSelect = document.getElementById('category-sidebar-select') as HTMLSelectElement | null;
        if (catSelect) catSelect.value = 'all';
        const brandSelect = document.getElementById('brand-select') as HTMLSelectElement | null;
        if (brandSelect) brandSelect.value = 'all';
        syncUrlAndSeo(true);
        fetchLiveProducts(true);
      }
    });
  }

  // 1. Integrated Categories Megamenu Dropdown
  const categoriesBtn = document.getElementById('categories-menu-btn');
  const megamenuPanel = document.getElementById('categories-megamenu-panel');

  if (categoriesBtn && megamenuPanel) {
    categoriesBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      state.categoriesMenuOpen = !state.categoriesMenuOpen;
      megamenuPanel.classList.toggle('hidden', !state.categoriesMenuOpen);
    });

    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#categories-dropdown-wrapper')) {
        state.categoriesMenuOpen = false;
        megamenuPanel.classList.add('hidden');
      }
    });
  }

  // Category buttons in Megamenu and on-page elements
  document.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('[data-nav-category]') as HTMLElement | null;
    if (target) {
      e.preventDefault();
      const catId = target.getAttribute('data-nav-category') || 'all';
      selectCategory(catId);
    }
  });

  // Category Sidebar Select
  const categorySidebarSelect = document.getElementById('category-sidebar-select') as HTMLSelectElement | null;
  if (categorySidebarSelect) {
    categorySidebarSelect.addEventListener('change', (e) => {
      const catId = (e.target as HTMLSelectElement).value;
      selectCategory(catId);
    });
  }

  // 2. Tiendas Button (Opens Stores Directory Modal)
  const navBtnStores = document.getElementById('nav-btn-stores');
  if (navBtnStores) {
    navBtnStores.addEventListener('click', (e) => {
      e.preventDefault();
      state.storesModalOpen = true;
      renderModals();
    });
  }

  // 3. Marcas Button & Sidebar Marcas Button (Opens Brands Directory Modal)
  const navBtnBrands = document.getElementById('nav-btn-brands');
  if (navBtnBrands) {
    navBtnBrands.addEventListener('click', (e) => {
      e.preventDefault();
      state.brandsModalOpen = true;
      renderModals();
    });
  }

  const sidebarOpenBrandsBtn = document.getElementById('sidebar-open-brands-btn');
  if (sidebarOpenBrandsBtn) {
    sidebarOpenBrandsBtn.addEventListener('click', (e) => {
      e.preventDefault();
      state.brandsModalOpen = true;
      renderModals();
    });
  }

  // 4. Turismo & Aduana Button (Opens Aduana Drawer)
  const navBtnCustoms = document.getElementById('nav-btn-customs');
  if (navBtnCustoms) {
    navBtnCustoms.addEventListener('click', (e) => {
      e.preventDefault();
      state.customsDrawerOpen = true;
      renderModals();
    });
  }

  // 5. Descuentos Button (Filters by lowest price & promotions)
  const navBtnDiscounts = document.getElementById('nav-btn-discounts');
  if (navBtnDiscounts) {
    navBtnDiscounts.addEventListener('click', (e) => {
      e.preventDefault();
      state.sortBy = 'price_asc';
      const sortSelect = document.getElementById('sort-select') as HTMLSelectElement | null;
      if (sortSelect) sortSelect.value = 'price_asc';
      syncUrlAndSeo(true);
      fetchLiveProducts(true);
    });
  }

  // Logo link reset home
  const logoLink = document.getElementById('header-logo-link');
  if (logoLink) {
    logoLink.addEventListener('click', (e) => {
      e.preventDefault();
      resetAllFilters();
    });
  }

  const viewGridBtn = document.getElementById('view-grid-btn');
  const viewListBtn = document.getElementById('view-list-btn');

  if (viewGridBtn && viewListBtn) {
    viewGridBtn.addEventListener('click', () => {
      state.viewMode = 'grid';
      viewGridBtn.className = 'p-1.5 rounded bg-slate-100 text-blue-700';
      viewListBtn.className = 'p-1.5 rounded text-slate-400 hover:text-slate-700';
      renderProductsGrid();
    });

    viewListBtn.addEventListener('click', () => {
      state.viewMode = 'list';
      viewListBtn.className = 'p-1.5 rounded bg-slate-100 text-blue-700';
      viewGridBtn.className = 'p-1.5 rounded text-slate-400 hover:text-slate-700';
      renderProductsGrid();
    });
  }

  const sortSelect = document.getElementById('sort-select') as HTMLSelectElement | null;
  if (sortSelect) {
    sortSelect.addEventListener('change', (e) => {
      state.sortBy = (e.target as HTMLSelectElement).value;
      syncUrlAndSeo(true);
      fetchLiveProducts(true);
    });
  }

  const brandSelect = document.getElementById('brand-select') as HTMLSelectElement | null;
  if (brandSelect) {
    brandSelect.addEventListener('change', (e) => {
      selectBrand((e.target as HTMLSelectElement).value);
    });
  }

  const applyPriceBtn = document.getElementById('apply-price-btn');
  if (applyPriceBtn) {
    applyPriceBtn.addEventListener('click', () => {
      const minEl = document.getElementById('min-price-input') as HTMLInputElement | null;
      const maxEl = document.getElementById('max-price-input') as HTMLInputElement | null;
      state.minPrice = parseFloat(minEl?.value || '0') || 0;
      state.maxPrice = parseFloat(maxEl?.value || '0') || 0;
      syncUrlAndSeo(true);
      renderProductsGrid();
    });
  }

  const instockToggle = document.getElementById('instock-toggle') as HTMLInputElement | null;
  if (instockToggle) {
    instockToggle.addEventListener('change', (e) => {
      state.inStockOnly = (e.target as HTMLInputElement).checked;
      renderProductsGrid();
    });
  }

  const resetFiltersBtn = document.getElementById('reset-filters-btn');
  if (resetFiltersBtn) {
    resetFiltersBtn.addEventListener('click', resetAllFilters);
  }

  const currencySelector = document.getElementById('currency-selector') as HTMLSelectElement | null;
  if (currencySelector) {
    currencySelector.addEventListener('change', (e) => {
      state.selectedCurrency = (e.target as HTMLSelectElement).value as CurrencyCode;
      renderProductsGrid();
      renderModals();
    });
  }

  const openTripCartBtn = document.getElementById('open-trip-cart-btn');
  if (openTripCartBtn) {
    openTripCartBtn.addEventListener('click', () => {
      state.tripCartOpen = true;
      renderModals();
    });
  }

  const openCustomsBtn = document.getElementById('open-customs-btn');
  if (openCustomsBtn) {
    openCustomsBtn.addEventListener('click', () => {
      state.customsDrawerOpen = true;
      renderModals();
    });
  }
}

// Initial Launch
initAppLayout();
initFromUrlParams();
renderSidebarBrandsSelect();
renderHeaderRatesTicker();
renderSidebarStores();
renderProductsGrid();
fetchEdgeRates();
fetchLiveProducts(true);

// Browser History Popstate Navigation (Back / Forward)
window.addEventListener('popstate', () => {
  initFromUrlParams();
  fetchLiveProducts(true);
});

// Auto-refresh currency rates every 60 seconds
setInterval(fetchEdgeRates, 60000);
